<script setup lang="ts">
/**
 * Paginador de bolsillo: flecha atrás, páginas en medio, flecha adelante.
 *
 * Vive aparte porque los rankings y los segmentos del panel lo comparten y
 * estaban resueltos cada uno a su manera. Este nació para los segmentos, donde
 * las tres columnas miden lo mismo y un paginador ancho las rompe: con texto
 * («Anterior» / «Siguiente») las tres cajas quedaban a alturas distintas y la
 * columna de más páginas empujaba a las otras dos.
 *
 * Las páginas son números y no un texto «Página 3 de 22»: con 22 páginas, saber
 * en cuál se está sin ver a cuántas está de ir al principio es de poca ayuda. Y
 * no se dibujan las 22: se dibuja una ventana de un máximo de cinco números
 * alrededor de la actual, con puntos suspensivos para lo que queda fuera.
 *
 * @prop page      Página actual.
 * @prop lastPage  Última página disponible.
 * @prop label     Nombre de lo que se pagina, para los rótulos accesibles.
 * @prop testid    Para que los tests apunten a este paginador concreto.
 *
 * @event change    La nueva página, solo si está dentro del rango.
 */

const props = withDefaults(
  defineProps<{
    page: number
    lastPage: number
    label: string
    testid?: string
  }>(),
  { testid: undefined },
)

const emit = defineEmits<{ change: [page: number] }>()

/**
 * Los números de página que se dibujan.
 *
 * Se recorta a una ventana de tres posiciones alrededor de la actual y se señalan
 * los huecos con puntos suspensivos, que no son botón: no hay nada a lo que ir.
 *
 * Son tres y no cinco porque el paginador vive en una columna que mide un tercio
 * del ancho: con cinco números más las dos flechas no cabe, y un paginador que
 * se parte en dos líneas ocupa más que lo que pagina. Tres dejan ver dónde se
 * está y a cuántas se está de los extremos, que es lo que se consulta.
 */
const paginasVisibles = (): number[] => {
  const inicio = Math.max(1, Math.min(props.lastPage - 2, props.page - 1))
  const numeros: number[] = []

  for (let indice = inicio; indice < inicio + 3 && indice <= props.lastPage; indice += 1) {
    numeros.push(indice)
  }

  return numeros
}

const irA = (pagina: number): void => {
  if (pagina < 1 || pagina > props.lastPage || pagina === props.page) {
    return
  }

  emit('change', pagina)
}
</script>

<template>
  <nav
    v-if="lastPage > 1"
    class="mt-3 flex items-center justify-center gap-1 border-t border-surface-100 pt-3 dark:border-surface-800"
    :aria-label="`Páginas de ${label}`"
    :data-testid="testid"
  >
    <!--
      Flecha de icono en vez de botón con texto: «Anterior» mide más que «3», y
      reservar ese ancho es lo que descuadraba las tres columnas. Los 44px son el
      objetivo táctil mínimo (WCAG 2.5.5) y no una decisión estética: un botón de
      icono pequeño exige puntero fino y con el dedo se falla.
    -->
    <button
      type="button"
      class="flex h-11 w-11 items-center justify-center rounded-full text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-surface-400 dark:hover:bg-surface-700 dark:hover:text-surface-100"
      :disabled="page <= 1"
      :aria-label="`Página anterior de ${label}`"
      @click="irA(page - 1)"
    >
      <i class="pi pi-chevron-left text-base" aria-hidden="true" />
    </button>

    <!--
      Suma de nº 3 + 2 flechas + 3 + 2 flechas de nuevo + el hueco: con la ventana
      de tres y los números a 32px, el conjunto cabe en el tercio de ancho de la
      columna sin partirse en dos líneas.
    -->
    <button
      v-for="numero in paginasVisibles()"
      :key="numero"
      type="button"
      class="flex h-11 min-w-8 items-center justify-center rounded px-1 text-xs font-medium tabular-nums transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
      :class="
        numero === page
          ? 'bg-primary-600 text-white'
          : 'text-surface-600 hover:bg-surface-100 dark:text-surface-300 dark:hover:bg-surface-700'
      "
      :aria-current="numero === page ? 'page' : undefined"
      @click="irA(numero)"
    >
      {{ numero }}
    </button>

    <!--
      Sale solo cuando la ventana no llega al final. Los puntos avisan de que hay
      más páginas; si no estuvieran, la lista parecería terminar en el 3.
    -->
    <span v-if="paginasVisibles().at(-1)! < lastPage" class="px-0.5 text-xs text-surface-400">
      …
    </span>

    <button
      type="button"
      class="flex h-11 w-11 items-center justify-center rounded-full text-surface-500 transition-colors hover:bg-surface-100 hover:text-surface-900 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:cursor-not-allowed disabled:opacity-40 dark:text-surface-400 dark:hover:bg-surface-700 dark:hover:text-surface-100"
      :disabled="page >= lastPage"
      :aria-label="`Página siguiente de ${label}`"
      @click="irA(page + 1)"
    >
      <i class="pi pi-chevron-right text-base" aria-hidden="true" />
    </button>
  </nav>
</template>
